/**
 * CSV Validation Utilities
 * Provides reusable functions for validating CSV structure and data
 */

// Driver CSV validation
export const validateDriverCsvStructure = (headers) => {
  const requiredHeaders = [
    'name', 'phone', 'phone_country_code', 'email', 'password', 
    'license_number', 'experience_level', 'current_latitude', 
    'current_longitude', 'availability_status'
  ];
  
  const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
  const extraHeaders = headers.filter(header => !requiredHeaders.includes(header));
  
  return {
    isValid: missingHeaders.length === 0,
    missingHeaders,
    extraHeaders,
    errors: [
      ...missingHeaders.map(h => `Missing required column: ${h}`),
      ...extraHeaders.map(h => `Unexpected column: ${h}`)
    ]
  };
};

export const validateDriverData = (driverData, rowIndex) => {
  const errors = [];
  
  if (!driverData.name?.trim()) {
    errors.push(`Row ${rowIndex + 1}: Name is required`);
  }
  
  if (!driverData.phone?.trim()) {
    errors.push(`Row ${rowIndex + 1}: Phone number is required`);
  } else if (!/^[6-9]\d{9}$/.test(driverData.phone.trim())) {
    errors.push(`Row ${rowIndex + 1}: Invalid phone number format`);
  }
  
  if (!driverData.password?.trim()) {
    errors.push(`Row ${rowIndex + 1}: Password is required`);
  } else if (driverData.password.trim().length < 8) {
    errors.push(`Row ${rowIndex + 1}: Password must be at least 8 characters`);
  }
  
  if (!driverData.license_number?.trim()) {
    errors.push(`Row ${rowIndex + 1}: License number is required`);
  }
  
  if (driverData.email?.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(driverData.email.trim())) {
      errors.push(`Row ${rowIndex + 1}: Invalid email format`);
    }
  }
  
  if (driverData.current_latitude && (isNaN(driverData.current_latitude) || driverData.current_latitude < -90 || driverData.current_latitude > 90)) {
    errors.push(`Row ${rowIndex + 1}: Invalid latitude value`);
  }
  
  if (driverData.current_longitude && (isNaN(driverData.current_longitude) || driverData.current_longitude < -180 || driverData.current_longitude > 180)) {
    errors.push(`Row ${rowIndex + 1}: Invalid longitude value`);
  }
  
  // Validate experience level (accept both uppercase and lowercase)
  const validExperienceLevels = ['BEGINNER', 'INTERMEDIATE', 'EXPERIENCED', 'EXPERT'];
  const validExperienceLevelsLower = ['beginner', 'intermediate', 'experienced', 'expert'];
  
  if (driverData.experience_level) {
    const experienceLevel = driverData.experience_level.trim();
    if (!validExperienceLevels.includes(experienceLevel) && !validExperienceLevelsLower.includes(experienceLevel)) {
      errors.push(`Row ${rowIndex + 1}: Invalid experience level. Must be one of: ${validExperienceLevels.join(', ')}`);
    }
  }
  
  return errors;
};

// Ambulance CSV validation
export const validateAmbulanceCsvStructure = (headers) => {
  const requiredHeaders = [
    'plate_number', 'type_id', 'current_latitude', 
    'current_longitude', 'status'
  ];
  
  const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
  const extraHeaders = headers.filter(header => !requiredHeaders.includes(header));
  
  return {
    isValid: missingHeaders.length === 0,
    missingHeaders,
    extraHeaders,
    errors: [
      ...missingHeaders.map(h => `Missing required column: ${h}`),
      ...extraHeaders.map(h => `Unexpected column: ${h}`)
    ]
  };
};

export const validateAmbulanceData = (ambulanceData, rowIndex) => {
  const errors = [];
  
  if (!ambulanceData.plate_number?.trim()) {
    errors.push(`Row ${rowIndex + 1}: Plate number is required`);
  } else if (ambulanceData.plate_number.trim().length < 5) {
    errors.push(`Row ${rowIndex + 1}: Plate number must be at least 5 characters`);
  }
  
  if (!ambulanceData.type_id?.trim()) {
    errors.push(`Row ${rowIndex + 1}: Ambulance type ID is required`);
  } else if (isNaN(ambulanceData.type_id)) {
    errors.push(`Row ${rowIndex + 1}: Type ID must be a valid number`);
  } else {
    const typeId = parseInt(ambulanceData.type_id);
    if (typeId < 1 || typeId > 7) {
      errors.push(`Row ${rowIndex + 1}: Type ID must be between 1 and 7`);
    }
  }
  
  if (ambulanceData.current_latitude && (isNaN(ambulanceData.current_latitude) || ambulanceData.current_latitude < -90 || ambulanceData.current_latitude > 90)) {
    errors.push(`Row ${rowIndex + 1}: Invalid latitude value`);
  }
  
  if (ambulanceData.current_longitude && (isNaN(ambulanceData.current_longitude) || ambulanceData.current_longitude < -180 || ambulanceData.current_longitude > 180)) {
    errors.push(`Row ${rowIndex + 1}: Invalid longitude value`);
  }
  
  const validStatuses = ['AVAILABLE', 'BUSY', 'MAINTENANCE', 'OUT_OF_SERVICE', 'ASSIGNED'];
  if (ambulanceData.status && !validStatuses.includes(ambulanceData.status.toUpperCase())) {
    errors.push(`Row ${rowIndex + 1}: Invalid status. Must be one of: ${validStatuses.join(', ')}`);
  }
  
  return errors;
};

// Check for duplicate plate numbers within the CSV
export const checkDuplicatePlateNumbers = (csvData) => {
  const plateNumbers = csvData.map(row => row.plate_number?.trim().toUpperCase()).filter(Boolean);
  const duplicates = plateNumbers.filter((plate, index) => plateNumbers.indexOf(plate) !== index);
  const uniqueDuplicates = [...new Set(duplicates)];
  
  if (uniqueDuplicates.length > 0) {
    return uniqueDuplicates.map(plate => `Duplicate plate number found: ${plate}`);
  }
  
  return [];
};

// Generic CSV parsing function
export const parseCsvFile = (csvContent) => {
  try {
    const lines = csvContent.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      throw new Error('CSV file must contain at least a header row and one data row');
    }
    
    // Parse headers
    const headerLine = lines[0];
    const headers = headerLine.split(',').map(h => h.trim().toLowerCase());
    console.log('📋 Raw headers:', headerLine.split(',').map(h => h.trim()));
    console.log('📋 Processed headers:', headers);
    
    // Parse data rows
    const data = lines.slice(1)
      .filter(line => line.trim())
      .map((line, index) => {
        const values = line.split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((header, headerIndex) => {
          obj[header] = values[headerIndex] || '';
        });
        console.log(`📋 Row ${index + 1} parsed:`, obj);
        return obj;
      });

    console.log('📋 Final parsed data:', data);
    return { headers, data };
  } catch (error) {
    throw new Error('Error parsing CSV file: ' + error.message);
  }
};

// CSV template generators
export const generateDriverCsvTemplate = () => {
  return "name,phone,phone_country_code,email,password,license_number,experience_level,current_latitude,current_longitude,availability_status\nRajesh Kumar,9876543210,+91,rajesh@example.com,password123,MH0220140001234,beginner,19.0760,72.8777,AVAILABLE\nPriya Sharma,9876543211,+91,priya@example.com,password123,MH0220140001235,intermediate,19.0760,72.8777,AVAILABLE\nAmit Singh,9876543212,+91,amit@example.com,password123,MH0220140001236,experienced,19.0760,72.8777,AVAILABLE";
};

export const generateAmbulanceCsvTemplate = (ambulanceTypes = []) => {
  // Use simple numbers (1, 2, 3...) instead of complex UUIDs
  // Generate unique plate numbers with timestamp to avoid duplicates
  const timestamp = Date.now().toString().slice(-6);
  let sampleRows = '';
  
  if (ambulanceTypes.length > 0) {
    // Use first 3 ambulance types with simple numbers
    const sampleTypes = ambulanceTypes.slice(0, 3);
    sampleRows = sampleTypes.map((type, index) => 
      `MH${timestamp.slice(0,2)}AB${1000 + index},${index + 1},19.0760,72.8777,AVAILABLE`
    ).join('\n');
  } else {
    // Fallback sample data with unique plate numbers
    sampleRows = `MH${timestamp.slice(0,2)}AB1234,1,19.0760,72.8777,AVAILABLE\nMH${timestamp.slice(0,2)}CD5678,2,19.0760,72.8777,AVAILABLE\nMH${timestamp.slice(0,2)}EF9012,3,19.0760,72.8777,AVAILABLE`;
  }
  
  return `plate_number,type_id,current_latitude,current_longitude,status\n${sampleRows}`;
};

// File download utility
export const downloadCsvFile = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};
